import { gsap } from "../../animations/gsapSetup.ts";
import { CourtSvg } from "../../components/basketball/CourtSvg.tsx";
import { useGsapScrollTrigger } from "../../hooks/useGsapScrollTrigger.ts";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";
import { useI18n } from "../../i18n/context.tsx";
import styles from "./LiveGameView.module.css";

/* Player positions on court (% based) */
interface CourtPlayer {
  number: number;
  name: string;
  bpm: number;
  top: string;
  left: string;
}

const COURT_PLAYERS: CourtPlayer[] = [
  { number: 2, name: "Rozier", bpm: 152, top: "20%", left: "22%" },
  { number: 7, name: "Ware", bpm: 144, top: "65%", left: "50%" },
  { number: 11, name: "Jaquez", bpm: 150, top: "55%", left: "25%" },
  { number: 13, name: "Adebayo", bpm: 158, top: "30%", left: "50%" },
  { number: 14, name: "Herro", bpm: 148, top: "55%", left: "75%" },
];

const BENCH_PLAYERS = [
  { number: 0, name: "Fontecchio" },
  { number: 22, name: "Wiggins" },
  { number: 24, name: "Powell" },
  { number: 45, name: "Mitchell" },
  { number: 50, name: "Goldin" },
];

const GAME_STATS = [
  { label: "FG%", value: "52.4" },
  { label: "PTS", value: "68" },
  { label: "AST", value: "18" },
  { label: "REB", value: "32" },
];

export function LiveGameView() {
  const { t } = useI18n();
  const reducedMotion = useReducedMotion();

  const ref = useGsapScrollTrigger((el) => {
    if (reducedMotion) return;

    const court = el.querySelector("[data-court]");
    const side = el.querySelector("[data-side]");
    const markers = el.querySelectorAll("[data-marker]");

    const tl = gsap.timeline({
      scrollTrigger: { trigger: el, start: "top 70%", toggleActions: "play none none reverse" },
    });

    if (court) {
      gsap.set(court, { opacity: 0, scale: 0.95 });
      tl.to(court, { opacity: 1, scale: 1, duration: 0.8, ease: "power3.out" }, 0);
    }

    gsap.set(markers, { scale: 0, opacity: 0 });
    tl.to(
      markers,
      { scale: 1, opacity: 1, duration: 0.4, stagger: 0.08, ease: "back.out(2)" },
      0.4,
    );

    if (side) {
      gsap.set(side, { x: 30, opacity: 0 });
      tl.to(side, { x: 0, opacity: 1, duration: 0.7, ease: "power3.out" }, 0.3);
    }
  }, []);

  return (
    <section ref={ref} className={styles.section}>
      <h2 className={styles.title}>{t.aiTeam.gameTitle}</h2>
      <p className={styles.desc}>{t.aiTeam.gameDesc}</p>

      <div className={styles.layout}>
        {/* Court with player markers */}
        <div className={styles.courtWrap} data-court>
          <CourtSvg showLabels={false} className={styles.courtSvg} />
          {COURT_PLAYERS.map((p) => (
            <div
              key={p.number}
              className={styles.playerMarker}
              style={{ top: p.top, left: p.left }}
              data-marker
            >
              <div className={styles.markerAvatar}>{p.number}</div>
              <span className={styles.markerBpm}>{p.bpm}</span>
            </div>
          ))}
        </div>

        {/* Side panel */}
        <div className={styles.sidePanel} data-side>
          <div className={styles.scoreBoard}>
            <span className={styles.scoreLabel}>{t.aiTeam.gameLiveScore}</span>
            <span className={styles.scoreValue}>{t.aiTeam.gameScoreValue}</span>
          </div>

          <div className={styles.statGrid}>
            {GAME_STATS.map((stat) => (
              <div key={stat.label} className={styles.statCard}>
                <span className={styles.statLabel}>{stat.label}</span>
                <span className={styles.statValue}>{stat.value}</span>
              </div>
            ))}
          </div>

          <div>
            <div className={styles.benchTitle}>{t.aiTeam.gameBenchLabel}</div>
            <div className={styles.benchList}>
              {BENCH_PLAYERS.map((p) => (
                <div key={p.number} className={styles.benchPlayer}>
                  <span className={styles.benchNumber}>#{p.number}</span>
                  <span>{p.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
